import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Search, 
  Filter, 
  Heart, 
  Star, 
  Shield, 
  MapPin,
  Briefcase,
  GraduationCap,
  X,
  Loader2,
  ChevronDown,
  Bookmark
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/vivah`;

export default function VivahMatchesPage({ user }) {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [constants, setConstants] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  
  const [filters, setFilters] = useState({
    age_min: 18,
    age_max: 45,
    religion: '',
    caste: '',
    state: '',
    city: '',
    education: '',
    profession: '',
    marital_status: '',
    manglik: '',
    with_photo: false,
    verified_only: false,
    sort_by: 'recent'
  });

  useEffect(() => {
    fetchConstants();
  }, []);

  useEffect(() => {
    if (user) {
      setPage(1);
      setProfiles([]);
      fetchProfiles(1, true);
    }
  }, [user, filters]);

  const fetchConstants = async () => {
    try {
      const response = await axios.get(`${API}/constants`);
      setConstants(response.data);
    } catch (error) {
      console.error('Failed to fetch constants:', error);
    }
  };

  const fetchProfiles = async (pageNum = 1, reset = false) => {
    if (pageNum === 1) setLoading(true);
    else setLoadingMore(true);
    
    try {
      const token = localStorage.getItem('dharmbook_token');
      const params = new URLSearchParams({
        token,
        page: pageNum,
        limit: 12,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v !== '' && v !== false)
        )
      });
      
      const response = await axios.get(`${API}/matches?${params}`);
      const data = response.data;
      
      if (reset) {
        setProfiles(data.profiles || []);
      } else {
        setProfiles(prev => [...prev, ...(data.profiles || [])]);
      }
      setTotal(data.total || 0);
      setHasMore(data.has_more || false);
    } catch (error) {
      console.error('Failed to fetch profiles:', error);
      if (error.response?.status === 400) {
        toast.error('Please create your profile first');
        navigate('/vivah/register');
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = () => {
    if (!loadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchProfiles(nextPage);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      age_min: 18,
      age_max: 45,
      religion: '',
      caste: '',
      state: '',
      city: '',
      education: '',
      profession: '',
      marital_status: '',
      manglik: '',
      with_photo: false,
      verified_only: false,
      sort_by: 'recent'
    });
  };

  const toggleShortlist = async (e, profileId) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.post(`${API}/shortlist/${profileId}?token=${token}`);
      
      setProfiles(prev => prev.map(p => 
        p.id === profileId ? { ...p, is_shortlisted: response.data.is_shortlisted } : p
      ));
      
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to update shortlist');
    }
  };

  const expressInterest = async (e, profileId) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.post(`${API}/interest/${profileId}?token=${token}`);
      
      setProfiles(prev => prev.map(p => 
        p.id === profileId ? { ...p, interest_status: 'pending' } : p
      ));
      
      toast.success('Interest sent successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send interest');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-rose-600 to-amber-600 text-white py-4 px-4 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/vivah')} className="p-2 hover:bg-white/10 rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold">Browse Matches</h1>
              <p className="text-rose-200 text-sm">{total} profiles found</p>
            </div>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded-full transition-colors ${showFilters ? 'bg-white/20' : 'hover:bg-white/10'}`}
          >
            <Filter className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white border-b shadow-lg">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800">Filters</h3>
              <button
                onClick={clearFilters}
                className="text-sm text-rose-600 hover:text-rose-700"
              >
                Clear All
              </button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Age Range</label>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    value={filters.age_min}
                    onChange={(e) => handleFilterChange('age_min', e.target.value)}
                    className="w-16 px-2 py-1.5 text-sm border rounded-lg"
                    min="18"
                    max="70"
                  />
                  <span className="text-gray-400">-</span>
                  <input
                    type="number"
                    value={filters.age_max}
                    onChange={(e) => handleFilterChange('age_max', e.target.value)}
                    className="w-16 px-2 py-1.5 text-sm border rounded-lg"
                    min="18"
                    max="70"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-xs text-gray-600 mb-1">Religion</label>
                <select
                  value={filters.religion}
                  onChange={(e) => handleFilterChange('religion', e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border rounded-lg"
                >
                  <option value="">Any</option>
                  {constants?.religions?.map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-xs text-gray-600 mb-1">Caste</label>
                <select
                  value={filters.caste}
                  onChange={(e) => handleFilterChange('caste', e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border rounded-lg"
                >
                  <option value="">Any</option>
                  {filters.religion && constants?.castes?.[filters.religion]?.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-xs text-gray-600 mb-1">State</label>
                <select
                  value={filters.state}
                  onChange={(e) => handleFilterChange('state', e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border rounded-lg"
                >
                  <option value="">Any</option>
                  {constants?.states?.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-xs text-gray-600 mb-1">Education</label>
                <select
                  value={filters.education}
                  onChange={(e) => handleFilterChange('education', e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border rounded-lg"
                >
                  <option value="">Any</option>
                  {constants?.education_levels?.map(e => (
                    <option key={e} value={e}>{e}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-xs text-gray-600 mb-1">Profession</label>
                <select
                  value={filters.profession}
                  onChange={(e) => handleFilterChange('profession', e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border rounded-lg"
                >
                  <option value="">Any</option>
                  {constants?.professions?.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.with_photo}
                  onChange={(e) => handleFilterChange('with_photo', e.target.checked)}
                  className="w-4 h-4 text-rose-600 rounded"
                />
                <span className="text-sm text-gray-700">With Photo Only</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.verified_only}
                  onChange={(e) => handleFilterChange('verified_only', e.target.checked)}
                  className="w-4 h-4 text-rose-600 rounded"
                />
                <span className="text-sm text-gray-700">Verified Only</span>
              </label>
              
              <div className="ml-auto flex items-center gap-2">
                <span className="text-sm text-gray-600">Sort by:</span>
                <select
                  value={filters.sort_by}
                  onChange={(e) => handleFilterChange('sort_by', e.target.value)}
                  className="px-3 py-1.5 text-sm border rounded-lg"
                >
                  <option value="recent">Recently Active</option>
                  <option value="relevance">Relevance</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Profiles Grid */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-rose-600 animate-spin" />
          </div>
        ) : profiles.length === 0 ? (
          <div className="text-center py-20">
            <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No profiles found</h3>
            <p className="text-gray-500">Try adjusting your filters to see more matches</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {profiles.map((profile) => (
                <div
                  key={profile.id}
                  onClick={() => navigate(`/vivah/profile/${profile.id}`)}
                  className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all cursor-pointer group"
                  data-testid={`profile-card-${profile.profile_id}`}
                >
                  {/* Photo */}
                  <div className="relative h-56 bg-gradient-to-br from-rose-100 to-amber-100">
                    {profile.profile_photo ? (
                      <img 
                        src={profile.profile_photo} 
                        alt={profile.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="w-28 h-28 bg-white rounded-full flex items-center justify-center shadow-lg">
                          <span className="text-5xl font-bold text-rose-300">
                            {profile.name?.charAt(0)}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {/* Badges */}
                    <div className="absolute top-3 left-3 flex flex-col gap-2">
                      {profile.is_verified && (
                        <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                          <Shield className="w-3 h-3" /> Verified
                        </span>
                      )}
                      {profile.is_premium && (
                        <span className="bg-amber-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                          <Star className="w-3 h-3" /> Premium
                        </span>
                      )}
                    </div>
                    
                    {/* Shortlist Button */}
                    <button
                      onClick={(e) => toggleShortlist(e, profile.id)}
                      className={`absolute top-3 right-3 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                        profile.is_shortlisted 
                          ? 'bg-amber-500 text-white' 
                          : 'bg-white/80 text-gray-600 hover:bg-white'
                      }`}
                    >
                      <Bookmark className={`w-5 h-5 ${profile.is_shortlisted ? 'fill-current' : ''}`} />
                    </button>
                    
                    {/* Profile ID */}
                    <div className="absolute bottom-3 right-3 bg-black/50 text-white text-xs px-2 py-1 rounded">
                      {profile.profile_id}
                    </div>
                  </div>
                  
                  {/* Info */}
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-bold text-gray-800 text-lg group-hover:text-rose-600 transition-colors">
                          {profile.name}, {profile.age}
                        </h3>
                        <p className="text-sm text-gray-500">{profile.height}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        profile.interest_status === 'accepted' 
                          ? 'bg-green-100 text-green-700'
                          : profile.interest_status === 'pending'
                          ? 'bg-amber-100 text-amber-700'
                          : profile.interest_status?.startsWith('received')
                          ? 'bg-rose-100 text-rose-700'
                          : 'hidden'
                      }`}>
                        {profile.interest_status === 'accepted' && 'Accepted'}
                        {profile.interest_status === 'pending' && 'Sent'}
                        {profile.interest_status?.startsWith('received') && 'Received'}
                      </span>
                    </div>
                    
                    {/* Details */}
                    <div className="space-y-1.5 text-sm text-gray-600 mb-4">
                      <p className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        {profile.city}, {profile.state}
                      </p>
                      <p className="flex items-center gap-2">
                        <Briefcase className="w-4 h-4 text-gray-400" />
                        {profile.profession}
                      </p>
                      <p className="flex items-center gap-2">
                        <GraduationCap className="w-4 h-4 text-gray-400" />
                        {profile.education}
                      </p>
                    </div>
                    
                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="bg-rose-50 text-rose-700 text-xs px-2 py-1 rounded-full">
                        {profile.religion}
                      </span>
                      {profile.caste && (
                        <span className="bg-amber-50 text-amber-700 text-xs px-2 py-1 rounded-full">
                          {profile.caste}
                        </span>
                      )}
                      <span className="bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-full">
                        {profile.income}
                      </span>
                    </div>
                    
                    {/* Action Button */}
                    {!profile.interest_status && (
                      <button
                        onClick={(e) => expressInterest(e, profile.id)}
                        className="w-full py-2.5 bg-gradient-to-r from-rose-500 to-amber-500 text-white font-medium rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2"
                        data-testid={`express-interest-${profile.profile_id}`}
                      >
                        <Heart className="w-4 h-4" />
                        Express Interest
                      </button>
                    )}
                    {profile.interest_status === 'received_pending' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/vivah/profile/${profile.id}`);
                        }}
                        className="w-full py-2.5 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-medium rounded-xl hover:shadow-lg transition-all"
                      >
                        View & Respond
                      </button>
                    )}
                    {profile.interest_status === 'accepted' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/vivah/chat/${profile.id}`);
                        }}
                        className="w-full py-2.5 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-medium rounded-xl hover:shadow-lg transition-all"
                      >
                        Start Chat
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Load More */}
            {hasMore && (
              <div className="text-center mt-8">
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="px-8 py-3 bg-white border-2 border-rose-500 text-rose-600 font-medium rounded-xl hover:bg-rose-50 transition-all disabled:opacity-50 flex items-center gap-2 mx-auto"
                >
                  {loadingMore ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      Load More
                      <ChevronDown className="w-5 h-5" />
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
